import { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { CheckCircle, XCircle, WhatsappLogo, CurrencyInr, Copy, ArrowsClockwise, DeviceMobile, Eye } from '@phosphor-icons/react';

function IntegrationsStatus() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testNum, setTestNum] = useState('');
  const [showWaTest, setShowWaTest] = useState(false);
  const [waResult, setWaResult] = useState(null);
  const [rzResult, setRzResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/admin/integrations/health');
      setHealth(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const webhookFullUrl = `${window.location.origin}/api/webhooks/razorpay-web`;

  const copyUrl = () => {
    navigator.clipboard?.writeText(webhookFullUrl).then(() => toast.success('Webhook URL copied'));
  };

  const sendTestWa = async () => {
    if (!testNum || testNum.replace(/\D/g, '').length < 10) {
      toast.error('Enter a 10-digit mobile number');
      return;
    }
    setTesting(true);
    setWaResult(null);
    try {
      const { data } = await api.post('/api/admin/integrations/test/whatsapp', { to: testNum });
      setWaResult(data);
      if (data.ok) toast.success('Test WhatsApp sent — check the recipient');
      else toast.error(`Failed: ${data.last_error || data.status}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  const testRazorpay = async () => {
    setTesting(true);
    setRzResult(null);
    try {
      const { data } = await api.post('/api/admin/integrations/test/razorpay');
      setRzResult(data);
      if (data.ok) toast.success('Razorpay connection successful');
      else toast.error(`Connection failed: ${data.message}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  if (loading || !health) {
    return <Layout><div className="text-center py-12">Loading...</div></Layout>;
  }

  const twilio = health.twilio || {};
  const razorpay = health.razorpay || {};
  const mobileInbox = health.mobile_inbox || {};
  const counts = health.counts || {};

  const copyText = (text, label = 'Copied') => {
    navigator.clipboard?.writeText(text).then(() => toast.success(label));
  };

  const revealMobileKey = async () => {
    try {
      const { data } = await api.get('/api/admin/integrations/mobile-inbox/api-key');
      copyText(data.api_key, 'API Key copied — paste it to the mobile team via a secure channel');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reveal API key');
    }
  };

  return (
    <Layout>
      <div className="space-y-6 pb-12 max-w-6xl">
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-3xl font-light tracking-tight text-zinc-950">Integrations</h1>
            <p className="text-sm text-zinc-500 mt-1">WhatsApp (Twilio) and Payment Links (Razorpay) configuration & live status.</p>
          </div>
          <Button data-testid="refresh-btn" variant="outline" onClick={load}>
            <ArrowsClockwise size={16} className="mr-1" /> Refresh
          </Button>
        </div>

        {/* Twilio card */}
        <Card className={`border rounded-sm ${twilio.ready ? 'border-emerald-300 bg-emerald-50/30' : 'border-amber-300 bg-amber-50/30'}`}>
          <CardHeader className="border-b border-zinc-200 flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <WhatsappLogo size={22} className="text-emerald-600" />
              Twilio WhatsApp
              {twilio.ready
                ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><CheckCircle size={12} className="mr-1" />READY</Badge>
                : <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100"><XCircle size={12} className="mr-1" />NOT CONFIGURED</Badge>}
            </CardTitle>
            <Button
              data-testid="test-wa-btn"
              size="sm"
              disabled={!twilio.ready}
              onClick={() => setShowWaTest(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Send Test
            </Button>
          </CardHeader>
          <CardContent className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <KV label="Account SID" value={twilio.account_sid || '—'} mono />
            <KV label="Auth Token" value={twilio.auth_token || '—'} mono />
            <KV label="From Number" value={twilio.from_number || '—'} mono />
            {twilio.missing?.length > 0 && (
              <div className="md:col-span-3 text-xs text-amber-800 bg-amber-100/60 border border-amber-200 rounded-sm p-3">
                <strong>Missing env vars:</strong>
                <ul className="list-disc ml-5 mt-1">
                  {twilio.missing.map((m) => <li key={m}><code className="bg-white/60 px-1">{m}</code></li>)}
                </ul>
                <div className="mt-2">Add these in the Emergent <strong>Deployment Panel → Secrets</strong>, then redeploy.</div>
              </div>
            )}
            <div className="md:col-span-3 grid grid-cols-3 gap-3 mt-2">
              <Stat label="Pending" value={counts.whatsapp?.pending || 0} color="amber" />
              <Stat label="Sent" value={counts.whatsapp?.sent || 0} color="emerald" />
              <Stat label="Failed" value={counts.whatsapp?.failed || 0} color="red" />
            </div>
          </CardContent>
        </Card>

        {/* Razorpay card */}
        <Card className={`border rounded-sm ${razorpay.ready ? 'border-emerald-300 bg-emerald-50/30' : 'border-amber-300 bg-amber-50/30'}`}>
          <CardHeader className="border-b border-zinc-200 flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <CurrencyInr size={22} className="text-blue-600" />
              Razorpay Payment Links
              {razorpay.ready
                ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><CheckCircle size={12} className="mr-1" />READY</Badge>
                : <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100"><XCircle size={12} className="mr-1" />NOT CONFIGURED</Badge>}
              {razorpay.webhook_ready
                ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Webhook</Badge>
                : <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Webhook Missing</Badge>}
            </CardTitle>
            <Button
              data-testid="test-rzp-btn"
              size="sm"
              disabled={!razorpay.ready || testing}
              onClick={testRazorpay}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Test Connection
            </Button>
          </CardHeader>
          <CardContent className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <KV label="Key ID" value={razorpay.key_id || '—'} mono />
            <KV label="Key Secret" value={razorpay.key_secret || '—'} mono />
            <KV label="Webhook Secret" value={razorpay.webhook_secret || '—'} mono />

            {/* Webhook URL */}
            <div className="md:col-span-3">
              <Label className="text-xs uppercase text-zinc-500">Webhook URL (register this in Razorpay Dashboard)</Label>
              <div className="flex gap-2 mt-1">
                <Input data-testid="webhook-url" readOnly value={webhookFullUrl} className="font-mono text-xs" />
                <Button data-testid="copy-webhook-btn" variant="outline" onClick={copyUrl}>
                  <Copy size={14} className="mr-1" /> Copy
                </Button>
              </div>
              <p className="text-[11px] text-zinc-500 mt-1">
                Subscribe to events: <code>payment_link.paid</code>, <code>payment_link.partially_paid</code>, <code>payment_link.expired</code>, <code>payment_link.cancelled</code>.
              </p>
            </div>

            {razorpay.missing?.length > 0 && (
              <div className="md:col-span-3 text-xs text-amber-800 bg-amber-100/60 border border-amber-200 rounded-sm p-3">
                <strong>Missing env vars:</strong>
                <ul className="list-disc ml-5 mt-1">
                  {razorpay.missing.map((m) => <li key={m}><code className="bg-white/60 px-1">{m}</code></li>)}
                </ul>
                <div className="mt-2">Add these in the Emergent <strong>Deployment Panel → Secrets</strong>, then redeploy.</div>
              </div>
            )}

            {rzResult && (
              <div className={`md:col-span-3 text-xs p-3 rounded-sm border ${rzResult.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-red-50 border-red-200 text-red-900'}`}>
                <strong>Test result:</strong> {rzResult.message} (HTTP {rzResult.status_code})
              </div>
            )}

            <div className="md:col-span-3 grid grid-cols-3 gap-3 mt-2">
              <Stat label="Pending" value={counts.payment_links?.pending || 0} color="amber" />
              <Stat label="Paid" value={counts.payment_links?.paid || 0} color="emerald" />
              <Stat label="Failed" value={counts.payment_links?.failed || 0} color="red" />
            </div>
          </CardContent>
        </Card>

        {/* Mobile Inbox card */}
        <Card className={`border rounded-sm ${mobileInbox.ready ? 'border-emerald-300 bg-emerald-50/30' : 'border-amber-300 bg-amber-50/30'}`}>
          <CardHeader className="border-b border-zinc-200 flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <DeviceMobile size={22} className="text-purple-600" />
              Mobile Inbox
              {mobileInbox.ready
                ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><CheckCircle size={12} className="mr-1" />READY</Badge>
                : <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100"><XCircle size={12} className="mr-1" />NOT CONFIGURED</Badge>}
            </CardTitle>
            {mobileInbox.ready && (
              <Button
                data-testid="reveal-mobile-key-btn"
                size="sm"
                onClick={revealMobileKey}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Eye size={14} className="mr-1" /> Reveal + Copy Key
              </Button>
            )}
          </CardHeader>
          <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <KV label="API Key (masked)" value={mobileInbox.api_key || '—'} mono />
            <KV label="Auth Header" value={mobileInbox.auth_header || 'X-API-Key'} mono />

            <div className="md:col-span-2">
              <Label className="text-xs uppercase text-zinc-500">Endpoints</Label>
              <div className="mt-2 space-y-1">
                {(mobileInbox.endpoints || []).map((e, i) => {
                  const fullUrl = `${window.location.origin}${e.path}`;
                  return (
                    <div key={i} className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px] font-mono">{e.method}</Badge>
                      <code className="text-xs bg-zinc-100 px-2 py-1 rounded-sm flex-1 font-mono">{fullUrl}</code>
                      <Button data-testid={`copy-endpoint-${i}`} variant="ghost" size="sm" onClick={() => copyText(fullUrl, 'URL copied')}>
                        <Copy size={12} />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>

            {mobileInbox.cors_origins && mobileInbox.cors_origins.length > 0 && (
              <div className="md:col-span-2">
                <Label className="text-xs uppercase text-zinc-500">Allowed CORS Origins</Label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {mobileInbox.cors_origins.map((o) => <Badge key={o} variant="outline" className="text-[10px] font-mono">{o}</Badge>)}
                </div>
              </div>
            )}

            {mobileInbox.missing?.length > 0 && (
              <div className="md:col-span-2 text-xs text-amber-800 bg-amber-100/60 border border-amber-200 rounded-sm p-3">
                <strong>Missing env vars:</strong>
                <ul className="list-disc ml-5 mt-1">
                  {mobileInbox.missing.map((m) => <li key={m}><code className="bg-white/60 px-1">{m}</code></li>)}
                </ul>
                <div className="mt-2">
                  Generate a strong random key locally:
                  <code className="block mt-1 bg-white/60 p-1 rounded-sm text-[11px]">{'python -c "import secrets; print(secrets.token_urlsafe(32))"'}</code>
                  Then add it in the Deployment Panel → Secrets and redeploy.
                </div>
              </div>
            )}

            <div className="md:col-span-2 grid grid-cols-4 gap-3 mt-2">
              <Stat label="JOs Received" value={mobileInbox.activity?.mobile_job_orders_stored || 0} color="emerald" />
              <Stat label="Quotations Received" value={mobileInbox.activity?.mobile_quotations_stored || 0} color="emerald" />
              <Stat label="Inbox Creates" value={mobileInbox.activity?.inbox_created || 0} color="amber" />
              <Stat label="Inbox Updates" value={mobileInbox.activity?.inbox_updated || 0} color="amber" />
            </div>

            <div className="md:col-span-2 text-xs text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-sm p-3">
              📘 Full integration contract for the mobile team: <code>/app/MOBILE_INBOX_INTEGRATION.md</code> — includes JSON schemas, curl examples, and retry semantics.
            </div>
          </CardContent>
        </Card>

        {/* Setup link */}
        <Card className="border-zinc-300 rounded-sm">
          <CardHeader><CardTitle className="text-base">Setup Guides</CardTitle></CardHeader>
          <CardContent className="p-6 text-sm space-y-2">
            <p>📘 <strong>Twilio + Razorpay:</strong> <code>/app/INTEGRATION_SETUP.md</code></p>
            <p>📱 <strong>Mobile Inbox (for mobile team):</strong> <code>/app/MOBILE_INBOX_INTEGRATION.md</code></p>
            <p className="text-zinc-600">
              After adding secrets in the Deployment Panel and redeploying, return to this page and run the test buttons. All badges should turn green.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Test WhatsApp Dialog */}
      <Dialog open={showWaTest} onOpenChange={setShowWaTest}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send Test WhatsApp</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>10-digit Mobile</Label>
              <Input
                data-testid="test-wa-num"
                placeholder="9876543210"
                value={testNum}
                onChange={(e) => setTestNum(e.target.value)}
              />
              <p className="text-[11px] text-zinc-500 mt-1">
                If using Twilio Sandbox, the recipient must first join your sandbox by sending the join code to the sandbox number.
              </p>
            </div>
            {waResult && (
              <div className={`text-xs p-3 rounded-sm border ${waResult.ok ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                <strong>Status:</strong> {waResult.status}<br />
                {waResult.provider_message_id && <><strong>Message SID:</strong> <code>{waResult.provider_message_id}</code><br /></>}
                {waResult.last_error && <><strong>Error:</strong> {waResult.last_error}</>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWaTest(false)}>Close</Button>
            <Button
              data-testid="test-wa-submit"
              onClick={sendTestWa}
              disabled={testing}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {testing ? 'Sending...' : 'Send Test'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function KV({ label, value, mono }) {
  return (
    <div>
      <Label className="text-xs uppercase text-zinc-500">{label}</Label>
      <div className={`text-zinc-950 text-sm mt-0.5 ${mono ? 'font-mono' : ''}`}>{value}</div>
    </div>
  );
}

function Stat({ label, value, color }) {
  const colorMap = {
    amber: 'text-amber-700 bg-amber-50 border-amber-200',
    emerald: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    red: 'text-red-700 bg-red-50 border-red-200',
  };
  return (
    <div className={`p-3 rounded-sm border ${colorMap[color] || ''}`}>
      <p className="text-[10px] uppercase tracking-wider">{label}</p>
      <p className="text-xl font-bold">{value}</p>
    </div>
  );
}

export default IntegrationsStatus;
export { IntegrationsStatus };
